import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "../styles/Register.css";
import logo from "../assets/primerise-logo.png";

const API_BASE = import.meta.env.VITE_API_BASE || "https://primerise.space";

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    referralCode: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refLocked, setRefLocked] = useState(false);

  // Auto referral code from link: /register?ref=ABC123
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");

    if (ref) {
      setFormData((prev) => ({
        ...prev,
        referralCode: ref.trim().toUpperCase(),
      }));
      setRefLocked(true);
    } else {
      setFormData((prev) => ({
        ...prev,
        referralCode: "",
      }));
      setRefLocked(false);
    }
  }, [location.search]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "referralCode" && refLocked) return;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const readJsonSafely = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedName = formData.name.trim();
    const trimmedEmail = formData.email.trim().toLowerCase();
    const trimmedPassword = formData.password.trim();
    const trimmedConfirmPassword = formData.confirmPassword.trim();
    const trimmedReferralCode = formData.referralCode.trim().toUpperCase();

    if (
      !trimmedName ||
      !trimmedEmail ||
      !trimmedPassword ||
      !trimmedConfirmPassword
    ) {
      alert("All fields are required");
      return;
    }

    if (trimmedPassword.length < 6) {
      alert("Password must be at least 6 characters");
      return;
    }

    if (trimmedPassword !== trimmedConfirmPassword) {
      alert("Passwords do not match");
      return;
    }

    try {
      setLoading(true);

      const requestBody = {
        name: trimmedName,
        email: trimmedEmail,
        password: trimmedPassword,
      };

      if (trimmedReferralCode) {
        requestBody.referralCode = trimmedReferralCode;
      }

      const response = await fetch(`${API_BASE}/api/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const data = await readJsonSafely(response);

      if (!response.ok) {
        alert(data?.message || "Registration failed");
        return;
      }

      alert("Registration successful");

      setFormData({
        name: "",
        email: "",
        password: "",
        confirmPassword: "",
        referralCode: "",
      });

      navigate("/login");
    } catch (error) {
      console.error("Register error:", error);
      alert("Server error. Please try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-page">
      <div className="register-container">
        <div className="register-brand">
          <img src={logo} alt="Primerise Logo" className="register-logo" />
        </div>

        <div className="register-card">
          <div className="register-card-top">
            <h2>Create Account</h2>
            <p>Join Primerise and start your digital earning journey.</p>
          </div>

          <form onSubmit={handleSubmit} className="register-form">
            <div className="input-group">
              <label>Full Name</label>
              <input
                type="text"
                name="name"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={handleChange}
                autoComplete="name"
              />
            </div>

            <div className="input-group">
              <label>Email Address</label>
              <input
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                autoComplete="email"
              />
            </div>

            <div className="input-group">
              <label>Referral Code</label>
              <input
                type="text"
                name="referralCode"
                placeholder="Enter referral code if you have one"
                value={formData.referralCode}
                onChange={handleChange}
                disabled={refLocked}
                className={refLocked ? "input-locked" : ""}
              />
            </div>

            <div className="input-group">
              <label>Password</label>
              <div className="password-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Create password"
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                />

                <button
                  type="button"
                  className="show-password-btn"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            <div className="input-group">
              <label>Confirm Password</label>
              <div className="password-wrapper">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  placeholder="Confirm password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  autoComplete="new-password"
                />

                <button
                  type="button"
                  className="show-password-btn"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                >
                  {showConfirmPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            <button type="submit" className="register-btn" disabled={loading}>
              {loading ? "Creating Account..." : "Register"}
            </button>
          </form>

          <div className="register-footer">
            <p>
              Already have an account?{" "}
              <Link to="/login" className="login-link">
                Login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;